import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Mountain } from "lucide-react";

const BijasanMataTemple = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/bijasan0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Bijasan Mata Temple",
    "description": "Sacred hilltop temple in Indore dedicated to Goddess Bijasan Mata, offering panoramic city views",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bijasan Hill",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 06:00-20:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.6",
      "reviewCount": "7500"
    }
  };

  const highlights = [
    {
      icon: <Mountain className="w-6 h-6" />,
      title: "Hilltop Location",
      description: "Beautifully situated on Bijasan Hill, offering stunning panoramic views of Indore city"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Sacred Goddess Temple",
      description: "Dedicated to Goddess Bijasan Mata, known for granting protection and blessings to devotees"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Pilgrimage Destination",
      description: "Important pilgrimage site attracting devotees seeking spiritual peace and divine blessings"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Scenic Beauty",
      description: "Perfect combination of spiritual significance and natural beauty with breathtaking views"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "6:00 AM - 8:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 7 AM, Evening 6 PM" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "1-2 hours (including climb)" },
    { label: "Facilities", value: "Stairs, Resting areas, View points" }
  ];

  const festivals = [
    { name: "Navratri", time: "March/October", special: "Nine days of grand celebrations and special prayers" },
    { name: "Chaitra Navratri", time: "March/April", special: "Spring festival with elaborate decorations" },
    { name: "Sharad Navratri", time: "September/October", special: "Main Navratri celebration with cultural programs" },
    { name: "Devi Paksha", time: "Various times", special: "Special goddess worship periods throughout the year" }
  ];

  const experiences = [
    {
      title: "Sunrise Views",
      description: "Witness breathtaking sunrise views over Indore city from the hilltop temple",
      bestTime: "6:00 AM - 7:00 AM",
      difficulty: "Moderate climb"
    },
    {
      title: "Spiritual Trek",
      description: "Combine physical exercise with spiritual journey climbing the sacred hill",
      bestTime: "Morning or evening",
      difficulty: "Moderate to easy"
    },
    {
      title: "Photography",
      description: "Capture stunning panoramic views of Indore and temple architecture",
      bestTime: "Golden hour",
      difficulty: "Easy"
    }
  ];

  const nearbyAttractions = [
    { name: "Khajrana Ganesh Temple", distance: "5 km", time: "15 mins" },
    { name: "Rajwada Palace", distance: "8 km", time: "20 mins" },
    { name: "Lal Bagh Palace", distance: "7 km", time: "18 mins" },
    { name: "Central Museum", distance: "6 km", time: "15 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Bijasan Mata Temple Indore - Hilltop Goddess Temple | Aaoghumen</title>
        <meta name="description" content="Visit Bijasan Mata Temple in Indore with Aaoghumen - sacred hilltop temple dedicated to Goddess Bijasan Mata with panoramic city views. Complete guide with timings, trekking info, and spiritual significance." />
        <meta name="keywords" content="bijasan mata temple indore, bijasan hill temple, indore hilltop temple, goddess bijasan mata, aaoghumen bijasan, indore trekking temple, bijasan mata timings, navratri bijasan temple" />
        
        <meta property="og:title" content="Bijasan Mata Temple Indore - Sacred Hilltop Shrine | Aaoghumen" />
        <meta property="og:description" content="Experience divine blessings at Bijasan Mata Temple, a sacred hilltop shrine offering spiritual peace and panoramic views of Indore city." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/bijasan-mata-temple" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/bijasan-mata-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-green-600 via-blue-500 to-purple-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Bijasan Mata Temple Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-green-300">Bijasan Mata Temple</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Embark on a spiritual journey to this sacred hilltop temple dedicated to Goddess Bijasan Mata, offering divine blessings and spectacular panoramic views of Indore.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.6 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>6 AM - 8 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Bijasan Mata Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Bijasan Mata Temple stands majestically atop Bijasan Hill, serving as one of Indore's most revered spiritual destinations that beautifully combines devotion with natural beauty. This sacred temple is dedicated to Goddess Bijasan Mata, who is worshipped as a protective deity and is believed to grant strength, courage, and divine protection to her devotees. The temple's elevated position not only adds to its spiritual significance but also offers visitors breathtaking panoramic views of the entire Indore cityscape.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The journey to the temple involves a moderate climb up the hill, which many devotees consider as part of their spiritual practice. The path to the temple is well-maintained with stairs and resting points, making it accessible for people of different ages and fitness levels. The climb itself becomes a meditative experience as visitors ascend through the peaceful natural surroundings, leaving behind the hustle and bustle of city life.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the spiritual significance of Goddess Bijasan Mata, experience the unique combination of physical and spiritual journey, and enjoy the spectacular views that make this temple a perfect destination for both devotees and nature lovers. The temple is especially popular during sunrise and sunset when the views are most magnificent and the spiritual atmosphere is most profound.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-green-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-green-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Experiences */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Experiences</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {experiences.map((experience, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-green-600 mb-4">{experience.title}</h3>
                  <p className="text-gray-600 mb-4">{experience.description}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-500">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{experience.bestTime}</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Mountain className="w-4 h-4 mr-2" />
                      <span>{experience.difficulty}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Celebrations</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-green-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Tips for Visitors */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Tips</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Climbing Tips</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Wear comfortable walking shoes with good grip</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Carry water bottle, especially during summer months</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Start early morning to avoid heat and crowds</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Use resting points available along the climb</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Photography Tips</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Best views during sunrise (6-7 AM) and sunset (6-7 PM)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Panoramic city views available from temple premises</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Respect temple rules regarding photography inside</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span>Clear weather offers best visibility for city views</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-green-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Bijasan area, then short walk to hill base</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available to hill base, ₹50-100 from city center</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Convenient option to reach hill base directly</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Limited parking available at hill base</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Route:</strong> Follow signs to Bijasan Hill from city</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Bijasan Mata Temple" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Spiritual Hilltop Experience</h2>
              <p className="text-xl mb-6">Visit Bijasan Mata Temple with guidance for the perfect spiritual and scenic experience</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Bijasan Hill, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-green-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Trek
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-green-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default BijasanMataTemple;